import {
  Component,
  OnInit,
  ViewContainerRef,
  AfterViewInit,
  NgZone,
  ChangeDetectorRef,
  OnDestroy
} from '@angular/core';
import { BreakpointState } from '@angular/cdk/layout';
import { Router, Event, NavigationEnd } from '@angular/router';
import { MatDialogRef, MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { AuthService } from 'src/app/auth/auth.service';
import { SearchModeService } from 'src/app/shared/services/search-mode.service';
import { ResponsiveService } from 'src/app/shared/services/responsive.service';
import { Auth0Service } from 'src/app/shared/services/auth0.service';
import { ProgressService } from 'src/app/shared/services/progress.service';
import { TeamService } from 'src/app/shared/services/team.service';
import { Team } from 'src/app/shared/models/team/team';
import { TeamAddComponent } from 'src/app/teams/team-add/team-add.component';
import { ModalConfirmationComponent } from 'src/shared-app-portal/components/modal-confirmation/modal-confirmation.component';
import _ from 'lodash';
import { Profile } from 'src/app/shared/models/profile';
import { AttributeService } from 'src/app/shared/services/attribute.service';
import { AttributeGroupService } from 'src/app/shared/services/attribute-group.service';
import { TagService } from 'src/app/shared/services/tag.service';
import { AppConfigService } from 'src/app/shared/services/app-config.service';
import { CustomViewService } from 'src/app/shared/services/custom-view.service';
import { BuildInfoCheckService } from 'src/app/shared/services/build-info-check.service';
import { WorkspaceService } from '../shared/services/workspace.service';
import { SystemUserEulaService } from '../shared/services/system-user-eula.service';
import { Observable, Subscription } from 'rxjs';
import { TenantModuleType } from '../shared/models/tenantModuleType';
import { map } from 'rxjs/operators';

declare function zE(a: string, b: string, c?: any): any;
@Component({
  selector: 'app-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss'],
  standalone: false
})
export class ToolbarComponent implements OnInit, AfterViewInit, OnDestroy {
  isLoading: boolean = false;
  get profile(): Profile {
    return this.authService.userProfile;
  }
  confirmationRef: MatDialogRef<ModalConfirmationComponent>;
  title = 'Lineup';
  displaySidenavButton: boolean = false;
  subs: Subscription[] = [];

  private numberOfTeamsToDisplay: number = 5;
  teams: Team[];
  dialogRef: MatDialogRef<TeamAddComponent>;
  public loggedInSystemUserHasLatestEula: boolean = true;
  public tenantModuleType = TenantModuleType;
  isLoggedIn: boolean = false;
  private toolsMenuTrigger: MatMenuTrigger;
  private toolsMenuCloseTimeout: ReturnType<typeof setTimeout>;
  private authMenuTrigger: MatMenuTrigger;
  private authMenuCloseTimeout: ReturnType<typeof setTimeout>;
  // buttons whose click has already been guarded against MatMenuTrigger's toggle-close (see suppressClickWhenMenuOpen)
  private guardedHoverMenuButtons = new WeakSet<HTMLElement>();
  constructor(
    private authService: AuthService,
    public appConfig: AppConfigService,
    private auth0Service: Auth0Service,
    private responsiveService: ResponsiveService,
    private searchModeService: SearchModeService,
    private router: Router,
    private dialog: MatDialog,
    private viewContainerRef: ViewContainerRef,
    private zone: NgZone,
    private changeDetectorRef: ChangeDetectorRef,
    private progressService: ProgressService,
    private teamService: TeamService,
    private attributeService: AttributeService,
    private attributeGroupService: AttributeGroupService,
    private tagService: TagService,
    private customViewService: CustomViewService,
    private buildInfoCheckService: BuildInfoCheckService,
    private workspaceService: WorkspaceService,
    private systemUserEulaService: SystemUserEulaService
  ) {}
  openWidget() {
    zE('webWidget', 'helpCenter:setSuggestions', { url: true });
    zE('webWidget', 'show');
    zE('webWidget', 'open');
  }
  ngOnInit() {
    this.subs.push(
      this.responsiveService.getCustomObserver().subscribe((state: BreakpointState) => {
        if (!(this.searchModeService.isTeamMode() || this.searchModeService.isTalentMode())) {
          this.hideSidenavButton();
        } else {
          if (state.matches) {
            this.hideSidenavButton();
          } else {
            this.showSidenavButton();
          }
        }
      }),

      this.router.events.subscribe((event: Event) => {
        if (event instanceof NavigationEnd) {
          if (event.url.includes('home')) {
            this.hideSidenavButton();
          } else if (this.responsiveService.isSmallScreen()) {
            this.showSidenavButton();
          }
        }
      }),

      this.authService.isAuthenticated$.subscribe((authenticated) => {
        if (authenticated) {
          // create a system user record for the current user, if it doesn't yet exist
          this.setupApplication();
        } else {
          this.authService.getNewToken().subscribe(() => {
            this.setupApplication();
          });
        }
      }),

      this.systemUserEulaService.hasLoggedInSystemUserAcceptedLatestEula.subscribe((data) => {
        this.loggedInSystemUserHasLatestEula = data;
      })
    );
  }

  ngAfterViewInit() {
    let comp = this;
    comp.progressService.isLoading.subscribe((isLoading) => {
      comp.isLoading = isLoading;
      comp.zone.run(() => {
        comp.changeDetectorRef.detectChanges();
      });
    });
  }

  setupApplication() {
    this.isLoggedIn = true;
    this.auth0Service.createCurrentUser().subscribe(() => {
      this.workspaceService.getCurrentWorkspaces();
      this.systemUserEulaService
        .get_has_logged_in_system_user_accepted_latest_eula()
        .subscribe((res) => {
          if (res != null) {
            if (res === false) {
              this.systemUserEulaService.setAttemptedRoute(location.pathname);
              this.router.navigateByUrl('/home');
            }
          }
        });
    });

    this.buildInfoCheckService.initBuildFileCheck();
    this.teamService.getTeams();
    this.attributeService.getAttributeDefinitions();
    this.attributeGroupService.getAll();
    this.tagService.getTags();
    this.customViewService.getCustomViews();
    this.setTeamsSubscription();
  }
  setTeamsSubscription() {
    this.teamService.teams.subscribe(
      (teams) => (this.teams = this.orderAndLimitTeams(teams, this.numberOfTeamsToDisplay))
    );
  }

  orderAndLimitTeams(teams: Team[], numberOfTeams: number): Team[] {
    return _.take(_.orderBy(teams, ['updatedOn'], ['desc']), numberOfTeams);
  }

  get canChangePassword(): Observable<boolean> {
    return this.authService.isAuthenticated$.pipe(
      map((isAuthenticated) => {
        if (!isAuthenticated) {
          return false;
        }
        return !_.isNil(this.profile) && !this.profile.isUsingSSO;
      })
    );
  }

  changePassword() {
    const comp = this;
    comp.confirmationRef = comp.dialog.open(ModalConfirmationComponent, {
      viewContainerRef: comp.viewContainerRef,
      data: {
        action: 'change your password',
        okButtonText: 'Change password',
        description: 'We will send you an email with instructions.',
        delete: false
      }
    });

    comp.confirmationRef.afterClosed().subscribe((result) => {
      if (result) {
        comp.auth0Service
          .changePassword()
          .subscribe(() =>
            comp.auth0Service.openSnackBar(
              'Please check your email for instructions on how to change your password.',
              true
            )
          );
      }
      comp.confirmationRef = null;
    });
  }

  isAdmin() {
    return this.authService.adminLevelAccess;
  }

  isManager() {
    return this.authService.managerLevelAccess;
  }

  private hideSidenavButton() {
    this.displaySidenavButton = false;
  }

  private showSidenavButton() {
    this.displaySidenavButton = true;
  }

  public toggleSidenav() {
    this.responsiveService.toggleSidenav();
  }

  onToolsMenuTriggerEnter(trigger: MatMenuTrigger, event: MouseEvent) {
    this.toolsMenuTrigger = trigger;
    this.cancelToolsMenuClose();
    this.suppressClickWhenMenuOpen(event.currentTarget as HTMLElement, () => this.toolsMenuTrigger);
    if (!trigger.menuOpen) {
      trigger.openMenu();
    }
  }

  onToolsMenuTriggerLeave() {
    this.scheduleToolsMenuClose();
  }

  cancelToolsMenuClose() {
    clearTimeout(this.toolsMenuCloseTimeout);
  }

  scheduleToolsMenuClose() {
    this.toolsMenuCloseTimeout = setTimeout(() => this.toolsMenuTrigger?.closeMenu(), 200);
  }

  onAuthMenuTriggerEnter(trigger: MatMenuTrigger, event: MouseEvent) {
    this.authMenuTrigger = trigger;
    this.cancelAuthMenuClose();
    this.suppressClickWhenMenuOpen(event.currentTarget as HTMLElement, () => this.authMenuTrigger);
    if (!trigger.menuOpen) {
      trigger.openMenu();
    }
  }

  onAuthMenuTriggerLeave() {
    this.scheduleAuthMenuClose();
  }

  cancelAuthMenuClose() {
    clearTimeout(this.authMenuCloseTimeout);
  }

  scheduleAuthMenuClose() {
    this.authMenuCloseTimeout = setTimeout(() => this.authMenuTrigger?.closeMenu(), 200);
  }

  // MatMenuTrigger's own click handler toggles the menu closed; since hover already opened it,
  // a real click right after (or Playwright's click(), which hovers then clicks) would immediately
  // close it. A capture-phase listener runs before that bubble-phase handler and can cancel it.
  private suppressClickWhenMenuOpen(
    element: HTMLElement,
    getTrigger: () => MatMenuTrigger | undefined
  ) {
    if (!element || this.guardedHoverMenuButtons.has(element)) {
      return;
    }
    this.guardedHoverMenuButtons.add(element);
    element.addEventListener(
      'click',
      (event: MouseEvent) => {
        if (getTrigger()?.menuOpen) {
          event.stopImmediatePropagation();
          event.preventDefault();
        }
      },
      true
    );
  }

  openDialog() {
    this.dialogRef = this.dialog.open(TeamAddComponent, {
      viewContainerRef: this.viewContainerRef
    });

    this.dialogRef.afterClosed().subscribe((teamId) => {
      if (teamId) {
        this.closeMenuOnSmallScreen();
        this.router.navigate(['/teams/' + teamId]);
      }
      this.dialogRef = null;
    });
  }

  closeMenuOnSmallScreen() {
    this.responsiveService.closeMenuOnSmallScreen();
  }

  activeTeams() {
    return _.filter(this.teams, function (team) {
      return !team.isArchived;
    });
  }
  get isTestModeEnabled(): boolean {
    return this.appConfig?.tenant?.settings?.isTestModeEnabled === true;
  }

  get isTenantStagingConnection(): boolean {
    return this.appConfig?.tenant?.connections?.includes('lineup-staging') ?? false;
  }

  logout() {
    this.authService.logout();
  }

  get isEnvironmentStaging(): boolean {
    return this.appConfig.config.staging;
  }

  ngOnDestroy() {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
